import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  Package,
  History,
  CreditCard,
  Home,
  LogOut,
  Menu,
  X,
  Shield,
  MessageSquare,
  Store,
  Layout,
  Puzzle,
  ShoppingCart,
  FileText,
  Receipt,
  Settings,
  Tag,
  Gift,
} from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/use-auth';
import { SEOHead } from '@/components/seo/seo-head';

const navigation = [
  { name: 'Dashboard', href: '/app/admin', icon: Home },
  { name: 'Planuri', href: '/app/admin/plans', icon: Package },
  { name: 'Modele', href: '/app/admin/templates', icon: Layout },
  { name: 'Subscripții', href: '/app/admin/subscriptions', icon: CreditCard },
  { name: 'Add-ons', href: '/app/admin/addons', icon: Puzzle },
  { name: 'Achiziții Add-ons', href: '/app/admin/addon-purchases', icon: ShoppingCart },
  { name: 'Vouchere', href: '/app/admin/vouchers', icon: Tag },
  { name: 'Cadouri', href: '/app/admin/gifts', icon: Gift },
  { name: 'Facturi', href: '/app/admin/invoices', icon: Receipt },
  { name: 'Setări facturare', href: '/app/admin/settings/billing', icon: Settings },
  { name: 'Furnizori', href: '/app/admin/marketplace', icon: Store },
  { name: 'Blog', href: '/app/admin/blog', icon: FileText },
  { name: 'Mesaje', href: '/app/admin/contact', icon: MessageSquare },
  { name: 'Jurnal audit', href: '/app/admin/audit-log', icon: History },
];

export function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/app/login');
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Admin Panel" noIndex />
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform lg:translate-x-0',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center gap-2 px-6 border-b">
            <Shield className="h-6 w-6 text-primary" />
            <span className="font-bold text-lg">Admin Panel</span>
            <Button
              variant="ghost"
              size="icon"
              className="ml-auto lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 space-y-1 px-3 py-4">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                end={item.href === '/app/admin'}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  )
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          {/* User section */}
          <div className="border-t p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.email?.[0]?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-muted-foreground">Administrator</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Deconectare
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b bg-background px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <div className="flex-1" />
          <Button variant="ghost" size="sm" onClick={() => navigate('/app/dashboard')}>
            <Home className="h-4 w-4 mr-2" />
            Înapoi la aplicație
          </Button>
        </header>

        {/* Page content */}
        <main>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
